import { useEffect, useRef, useState } from "react"
import {
  useAdForgeGetBrandKitQuery,
  type OverlayDoc,
} from "~/ui-rtk/api/adForgeApi"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  ensureFontsLoaded,
  fontFamiliesInDoc,
  renderOverlay,
} from "./render-overlay"
import { useCustomBrandFonts } from "./useCustomBrandFonts"

/**
 * Live, non-destructive overlay preview: renders the card's assigned layout
 * doc to a transparent canvas layered over the media. `object-cover` matches
 * the media element underneath, so the preview crops identically. Display
 * only — this canvas is never exported, so a (potentially tainting)
 * cross-origin logo draw is fine here.
 */

interface CardOverlayPreviewProps {
  doc: OverlayDoc
  /**
   * MUST match the media element underneath: the overlay is baked INTO the
   * media, so wherever the media letterboxes (object-contain in placement
   * tiles), the overlay letterboxes with it — never onto the padding bars.
   */
  fit?: "cover" | "contain"
  className?: string
}

export default function CardOverlayPreview({
  doc,
  fit = "cover",
  className,
}: CardOverlayPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Register uploaded brand fonts before drawing (module-deduped).
  useCustomBrandFonts()
  const { data: brandKit } = useAdForgeGetBrandKitQuery()
  const { data: logoAsset } = useGetAssetQuery(brandKit?.logoAssetId ?? "", {
    skip: !brandKit?.logoAssetId,
  })
  const logoUrl = logoAsset?.thumbnailUrl || logoAsset?.url || null
  const [logo, setLogo] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    if (!logoUrl) {
      setLogo(null)
      return
    }
    let cancelled = false
    const img = new Image()
    img.onload = () => {
      if (!cancelled) setLogo(img)
    }
    img.src = logoUrl
    return () => {
      cancelled = true
    }
  }, [logoUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    const draw = (): void => {
      if (cancelled || !canvasRef.current) return
      canvas.width = doc.canvas.width
      canvas.height = doc.canvas.height
      const ctx = canvas.getContext("2d")
      if (!ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      renderOverlay(ctx, doc, 1, { logo })
    }
    // First paint immediately (fallback fonts), repaint once brand fonts land.
    draw()
    void ensureFontsLoaded(fontFamiliesInDoc(doc)).then(draw)
    return () => {
      cancelled = true
    }
  }, [doc, logo])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full",
        fit === "contain" ? "object-contain" : "object-cover",
        className,
      )}
    />
  )
}
